import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Fa from 'react-fontawesome';
import moment from 'moment';
import pluralize from 'pluralize';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import NumberDisplay from '../../../../components/number-display';
import MoneyDisplay from '../../../../components/money-display';
import DateTimeDisplay from '../../../../components/date-time-display';
import ProgressBar from '../../../../components/progress-bar';
import Counter from '../../../../components/counter';
import styles from './styles.css';
import {
  getStatsLoading,
  getStatsLoaded,
  getStats,
  getConversionGraphLoading,
  getConversionGraphLoaded,
} from '../../PipelineReducer';

class PipelineStats extends Component {
  constructor() {
    super();
    this.state = {};
  }

  getArrow(value) {
    let arrowComp = null;
    if (value > 0) {
      arrowComp = (
        <span>&nbsp;<Fa name="long-arrow-up" className="text-success" /></span>
      );
    } else if (value < 0) {
      arrowComp = (
        <span>&nbsp;<Fa name="long-arrow-down" className="text-danger" /></span>
      );
    }
    return arrowComp;
  }

  renderSizeStats() {
    const { stats, pipelineDate } = this.props;
    const sizeTip = (
      <Tooltip id="popover-size">
        Current size of the pipeline as of <DateTimeDisplay value={pipelineDate} format="short" />
      </Tooltip>
    );
    const opportunity = pluralize('opportunity', stats.count);

    return (
      <div className={`${styles.panel} panel panel-default animated flipInX`}>
        <div className={`${styles.panelBody} panel-body animated slideInUp`}>
          <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={sizeTip}>
            <i className={`${styles.tipTrigger} animated fadeIn`}><Fa name="ellipsis-h" /></i>
          </OverlayTrigger>
          <h3>Size</h3>
          <div className={styles.counter}>
            <Counter value={stats.amount}>
              <MoneyDisplay value={stats.amount} abbreviated precision={1} />
            </Counter>
          </div>
          <h4>Current:</h4>
          <ul className={styles.list}>
            <li>
              <NumberDisplay value={stats.count} precision={0} /> {opportunity}
            </li>
            <li>
              <MoneyDisplay value={stats.average} abbreviated precision={1} /> average opportunity size
            </li>
          </ul>
        </div>
      </div>
    );
  }

  renderNetChangeStats() {
    const { stats, pipelineDate } = this.props;
    const netChange = (stats.netChangeIncreaseAmount || 0) - (stats.netChangeDecreaseAmount || 0);

    const netChangeTip = (
      <Tooltip id="popover-net-change">
        Net amount of opportunities that have increased or decreased since <DateTimeDisplay value={pipelineDate} format="short" />, not including new opportunities
      </Tooltip>
    );
    const increasedOpp = pluralize('opportunity', stats.netChangeIncreaseCount);
    const decreasedOpp = pluralize('opportunity', stats.netChangeDecreaseCount);
    const arrowComp = this.getArrow(netChange);
    return (
      <div className={`${styles.panel} panel panel-default animated flipInX`}>
        <div className={`${styles.panelBody} panel-body animated slideInUp`}>
          <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={netChangeTip}>
            <i className={`${styles.tipTrigger} animated fadeIn`}><Fa name="ellipsis-h" /></i>
          </OverlayTrigger>
          <h3>Net Change</h3>
          <div className={styles.counter}>
            <Counter value={netChange}>
              <MoneyDisplay value={netChange} abbreviated precision={1} withPlus />
            </Counter>{arrowComp}
          </div>
          <h4>Changes:</h4>
          <ul className={styles.list}>
            {stats.netChangeIncreaseCount > 0 &&
              <li>
                <MoneyDisplay value={stats.netChangeIncreaseAmount} abbreviated precision={1} /> increased
                 in <NumberDisplay value={stats.netChangeIncreaseCount} precision={0} /> {increasedOpp}
              </li>
            }
            {stats.netChangeIncreaseCount === 0 &&
              <li className={styles.notAvaiable}>
                No increase since <DateTimeDisplay value={pipelineDate} format="short" />
              </li>
            }
            {stats.netChangeDecreaseCount > 0 &&
              <li>
                <span className="text-danger">
                  <MoneyDisplay value={stats.netChangeDecreaseAmount} abbreviated precision={1} />
                </span> decreased in <NumberDisplay value={stats.netChangeDecreaseCount} precision={0} /> {decreasedOpp}
              </li>
            }
            {stats.netChangeDecreaseCount === 0 &&
              <li className={styles.notAvaiable}>
                No decrease since <DateTimeDisplay value={pipelineDate} format="short" />
              </li>
            }
          </ul>
        </div>
      </div>
    );
  }

  renderNetGrowthStats() {
    const { stats, pipelineDate } = this.props;
    const netGrowthTip = (
      <Tooltip id="popover-net-growth">
        Net new opportunities minus closed opportunities since <DateTimeDisplay value={pipelineDate} format="short" />
      </Tooltip>
    );
    const netChange = (stats.netGrowthNewAmount || 0) - (stats.netGrowthClosedAmount || 0);
    const increasedOpp = pluralize('opportunity', stats.netGrowthNewCount);
    const decreasedOpp = pluralize('opportunity', stats.netGrowthClosedCount);
    const arrowComp = this.getArrow(netChange);
    return (
      <div className={`${styles.panel} panel panel-default animated flipInX`}>
        <div className={`${styles.panelBody} panel-body animated slideInUp`}>
          <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={netGrowthTip}>
            <i className={`${styles.tipTrigger} animated fadeIn`}><Fa name="ellipsis-h" /></i>
          </OverlayTrigger>
          <h3>Net Growth</h3>
          <div className={styles.counter}>
            <Counter value={netChange}>
              <MoneyDisplay value={netChange} abbreviated precision={1} withPlus />
            </Counter>{arrowComp}
          </div>
          <h4>Changes:</h4>
          <ul className={styles.list}>
            {stats.netGrowthNewCount > 0 &&
              <li>
                <MoneyDisplay value={stats.netGrowthNewAmount} abbreviated precision={1} /> in <NumberDisplay value={stats.netGrowthNewCount} precision={0} /> new {increasedOpp}
              </li>
            }
            {stats.netGrowthNewCount === 0 &&
              <li className={styles.notAvaiable}>
                No new opportunities since <DateTimeDisplay value={pipelineDate} format="short" />
              </li>
            }
            {stats.netGrowthClosedCount > 0 &&
              <li>
                <span className="text-danger">
                  <MoneyDisplay value={stats.netGrowthClosedAmount} abbreviated precision={1} />
                </span> in <NumberDisplay value={stats.netGrowthClosedCount} precision={0} /> closed {decreasedOpp}
              </li>
            }
            {stats.netGrowthClosedCount === 0 &&
              <li className={styles.notAvaiable}>
                No closed opportunities since <DateTimeDisplay value={pipelineDate} format="short" />
              </li>
            }
          </ul>
        </div>
      </div>
    );
  }

  renderNetAdvancedStats() {
    const { stats, pipelineDate } = this.props;
    const netAdvancedTip = (
      <Tooltip id="popover-net-advanced">
        Pipeline amount that advanced or regressed since <DateTimeDisplay value={pipelineDate} format="short" />.
        Advanced = moved forward in the sales process. Regressed = moved backward in the sales process.
      </Tooltip>
    );
    const netChange = (stats.netAdvancedAmount || 0) - (stats.netRegressedAmount || 0);
    const increasedOpp = pluralize('opportunity', stats.netAdvancedCount);
    const decreasedOpp = pluralize('opportunity', stats.netRegressedCount);
    const arrowComp = this.getArrow(netChange);
    return (
      <div className={`${styles.panel} panel panel-default animated flipInX`}>
        <div className={`${styles.panelBody} panel-body animated slideInUp`}>
          <OverlayTrigger trigger={['hover', 'focus']} placement="right" overlay={netAdvancedTip}>
            <i className={`${styles.tipTrigger} animated fadeIn`}><Fa name="ellipsis-h" /></i>
          </OverlayTrigger>
          <h3>Net Advanced</h3>
          <div className={styles.counter}>
            <Counter value={netChange}>
              <MoneyDisplay value={netChange} abbreviated precision={1} withPlus />
            </Counter>{arrowComp}
          </div>
          <h4>Changes:</h4>
          <ul className={styles.list}>
            {stats.netAdvancedCount > 0 &&
              <li>
                <MoneyDisplay value={stats.netAdvancedAmount} abbreviated precision={1} /> advanced
                  in <NumberDisplay value={stats.netAdvancedCount} precision={0} /> {increasedOpp}
              </li>
            }
            {stats.netAdvancedCount === 0 &&
              <li className={styles.notAvaiable}>
                No opportunities have advanced since <DateTimeDisplay value={pipelineDate} format="short" />
              </li>
            }
            {stats.netRegressedCount > 0 &&
              <li>
                <span className="text-danger">
                  <MoneyDisplay value={stats.netRegressedAmount} abbreviated precision={1} />
                </span> regressed in <NumberDisplay value={stats.netRegressedCount} precision={0} /> {decreasedOpp}
              </li>
            }
            {stats.netRegressedCount === 0 &&
              <li className={styles.notAvaiable}>
                No opportunities have regressed  since <DateTimeDisplay value={pipelineDate} format="short" />
              </li>
            }
          </ul>
        </div>
      </div>
    );
  }

  renderTotalDelayedStats() {
    const { stats, pipelineDate } = this.props;
    const delayedTip = (
      <Tooltip id="popover-delayed">
        Number of opportunities that have had their close date pushed to a later date since <DateTimeDisplay value={pipelineDate} format="short" />.
      </Tooltip>
    );
    const netChange = stats.delayedCount || 0;
    const deals = pluralize('Deal', netChange);
    return (
      <div className={`${styles.panel} panel panel-default animated flipInX`}>
        <div className={`${styles.panelBody} panel-body animated slideInUp`}>
          <OverlayTrigger trigger={['hover', 'focus']} placement="left" overlay={delayedTip}>
            <i className={`${styles.tipTrigger} animated fadeIn`}><Fa name="ellipsis-h" /></i>
          </OverlayTrigger>
          <h3>Total Delayed</h3>
          <div className={styles.counter}>
            <Counter value={netChange}>
              <NumberDisplay value={netChange} precision={0} />
            </Counter>&nbsp;{deals}
          </div>
          <h4>Changes:</h4>
          <ul className={styles.list}>
            {stats.delayedCount > 0 &&
              <li>
                <MoneyDisplay value={stats.delayedAmount} abbreviated precision={1} /> with delayed close dates
              </li>
            }
            {stats.delayedCount === 0 &&
              <li className={styles.notAvaiable}>
                No delayed opportunities since <DateTimeDisplay value={pipelineDate} format="short" />
              </li>
            }
            {stats.delayedCount > 0 &&
              <li>
                <NumberDisplay value={stats.delayed_average} precision={0} /> days delayed on average
              </li>
            }
          </ul>
        </div>
      </div>
    );
  }

  renderProjectedSalesStats() {
    const { stats, conversionLoaded, conversionLoading } = this.props;
    const firstRate = stats.firstProbabilityRate;
    const projectedTip = (
      <Tooltip id="projected-delayed">
        <div>
          {conversionLoading && !conversionLoaded &&
            <Fa name="spinner" spin />
          }
          {!conversionLoading && conversionLoaded &&
            <div>
              Projected sales are based on each sales stages&rsquo; win probability in the last 12 months.
              Overall win probability is <NumberDisplay value={firstRate * 100} precision={1} suffix="%" />.
            </div>
          }
        </div>
      </Tooltip>
    );


    return (
      <div className={`${styles.panel} panel panel-default animated flipInX`}>
        <div className={`${styles.panelBody} panel-body animated slideInUp`}>
          <OverlayTrigger trigger={['hover', 'focus']} placement="left" overlay={projectedTip}>
            <i className={`${styles.tipTrigger} animated fadeIn`}><Fa name="ellipsis-h" /></i>
          </OverlayTrigger>
          <h3>Projected Sales</h3>
          <div className={styles.counter}>
            {conversionLoading && !conversionLoaded &&
              <Fa name="spinner" spin />
            }
            {!conversionLoading && conversionLoaded &&
              <Counter value={stats.totalProjectedSales}>
                <MoneyDisplay value={stats.totalProjectedSales} abbreviated precision={1} />
              </Counter>
            }
          </div>
          <h4>Changes:</h4>
          {conversionLoading && !conversionLoaded &&
            <ProgressBar />
          }
          {!conversionLoading && conversionLoaded &&
            <ul className={styles.list}>
              <li>
                <MoneyDisplay value={stats.earlyProjectedSales} abbreviated precision={1} /> from early sales stages
              </li>
              <li>
                <MoneyDisplay value={stats.lateProjectedSales} abbreviated precision={1} /> from late sales stages
              </li>
            </ul>
          }
        </div>
      </div>
    );
  }

  render() {
    const { loading, loaded } = this.props;
    return (
      <div>
        {(loading || !loaded) && <ProgressBar />}
        {!loading && loaded &&
          <div className={styles.stats}>
            {this.renderSizeStats()}
            {this.renderNetChangeStats()}
            {this.renderNetGrowthStats()}
            {this.renderNetAdvancedStats()}
            {this.renderTotalDelayedStats()}
            {this.renderProjectedSalesStats()}
          </div>
        }
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    loading: getStatsLoading(state),
    loaded: getStatsLoaded(state),
    conversionLoading: getConversionGraphLoading(state),
    conversionLoaded: getConversionGraphLoaded(state),
    stats: getStats(state),
  };
}

PipelineStats.propTypes = {
  loaded: PropTypes.bool,
  loading: PropTypes.bool,
  conversionLoading: PropTypes.bool,
  conversionLoaded: PropTypes.bool,
  stats: PropTypes.shape({}),
  pipelineDate: PropTypes.instanceOf(moment).isRequired,
};

PipelineStats.defaultProps = {
  loaded: false,
  loading: false,
  conversionLoading: false,
  conversionLoaded: false,
  stats: {},
};
PipelineStats.contextTypes = {
  router: PropTypes.object,
  addNotification: PropTypes.func.isRequired,
};
export default connect(mapStateToProps)(PipelineStats);
